import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logoContainer}>
            <div style={styles.logoIcon}>
              <img src="/logo.png" alt="BloomCube AI" style={styles.logoImage} />
            </div>
            <div style={styles.logoText}>
              <span style={styles.logoTitle}>BloomCube AI</span>
              <span style={styles.logoBadge}>Admin</span>
            </div>
          </div>
        </div>

        <nav style={styles.sidebarNav}>
          <div style={styles.navSection}>
            <span style={styles.navSectionTitle}>Overview</span>
            <NavLink to="/admin" end style={({ isActive }) => ({
              ...styles.sidebarLink,
              ...(isActive ? styles.sidebarLinkActive : {}),
            })}>
              <span style={styles.linkIcon}>📊</span>
              <span>Dashboard</span>
              {/* Active indicator */}
            </NavLink>
          </div>

          <div style={styles.navSection}>
            <span style={styles.navSectionTitle}>Management</span>
            <NavLink to="/admin/upload" style={({ isActive }) => ({
              ...styles.sidebarLink,
              ...(isActive ? styles.sidebarLinkActive : {}),
            })}>
              <span style={styles.linkIcon}>🎬</span>
              <span>Video Upload</span>
            </NavLink>
            <NavLink to="/admin/cubicles" style={({ isActive }) => ({
              ...styles.sidebarLink,
              ...(isActive ? styles.sidebarLinkActive : {}),
            })}>
              <span style={styles.linkIcon}>🗺️</span>
              <span>Cubicle Config</span>
            </NavLink>
          </div>
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.systemStatus}>
            <div style={styles.statusDot} />
            <span>System Online</span>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div style={styles.mainArea}>
        {/* Top Header */}
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.pageTitle}>Admin Control Center</h1>
            <p style={styles.pageSubtitle}>Monitor and manage workplace wellness</p>
          </div>

          <div style={styles.headerRight}>
            <div style={styles.headerStats}>
              <div style={styles.headerStat}>
                <span style={styles.headerStatIcon}>👥</span>
                <span style={styles.headerStatLabel}>Monitoring</span>
              </div>
            </div>

            <div 
              style={styles.userProfile}
              onClick={() => setIsProfileOpen(!isProfileOpen)}
            >
              <div style={styles.avatar}>
                {user?.name?.charAt(0).toUpperCase() || 'A'}
              </div>
              <div style={styles.userInfo}>
                <span style={styles.userName}>{user?.name}</span>
                <span style={styles.userRole}>Administrator</span>
              </div>
              <span style={styles.dropdownIcon}>▾</span>

              {isProfileOpen && (
                <div style={styles.dropdown}>
                  <div style={styles.dropdownHeader}>
                    <div style={styles.dropdownAvatar}>
                      {user?.name?.charAt(0).toUpperCase() || 'A'}
                    </div>
                    <div>
                      <div style={styles.dropdownName}>{user?.name}</div>
                      <div style={styles.dropdownEmail}>{user?.email}</div>
                    </div>
                  </div>
                  <div style={styles.dropdownDivider} />
                  <button onClick={handleLogout} style={styles.logoutBtn}>
                    <span>🚪</span>
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main style={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F8FAFC',
  },
  sidebar: {
    width: '280px',
    background: 'linear-gradient(180deg, #1E1B4B 0%, #312E81 100%)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
  },
  sidebarHeader: {
    padding: '24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  logoIcon: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImage: {
    width: '48px',
    height: '48px',
    objectFit: 'contain',
  },
  logoText: {
    display: 'flex',
    flexDirection: 'column',
  },
  logoTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'white',
  },
  logoBadge: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#A78BFA',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  sidebarNav: {
    flex: 1,
    padding: '24px 16px',
    overflowY: 'auto',
  },
  navSection: {
    marginBottom: '28px',
  },
  navSectionTitle: {
    display: 'block',
    fontSize: '11px',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.4)',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginBottom: '12px',
    paddingLeft: '12px',
  },
  sidebarLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '12px',
    textDecoration: 'none',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    marginBottom: '4px',
  },
  sidebarLinkActive: {
    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
    color: 'white',
    boxShadow: '0 4px 15px rgba(124, 58, 237, 0.4)',
  },
  linkIcon: {
    fontSize: '18px',
  },
  sidebarFooter: {
    padding: '20px 24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  systemStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#10B981',
    boxShadow: '0 0 10px rgba(16, 185, 129, 0.6)',
    animation: 'pulse 2s ease-in-out infinite',
  },
  mainArea: {
    flex: 1,
    marginLeft: '280px',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 32px',
    background: 'white',
    borderBottom: '1px solid #E5E7EB',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  headerLeft: {},
  pageTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1E1B4B',
    margin: 0,
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '4px 0 0',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  headerStats: {
    display: 'flex',
    gap: '16px',
  },
  headerStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    background: '#F3F4F6',
    borderRadius: '10px',
  },
  headerStatIcon: {
    fontSize: '16px',
  },
  headerStatLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#4B5563',
  },
  userProfile: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    borderRadius: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    border: '1px solid #E5E7EB',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '16px',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  userRole: {
    fontSize: '12px',
    color: '#7C3AED',
    fontWeight: 500,
  },
  dropdownIcon: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    width: '280px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    animation: 'fadeIn 0.2s ease-out',
    zIndex: 200,
  },
  dropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'linear-gradient(135deg, #EDE9FE 0%, #F5F3FF 100%)',
  },
  dropdownAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '18px',
  },
  dropdownName: {
    fontWeight: 600,
    color: '#1F2937',
    fontSize: '15px',
  },
  dropdownEmail: {
    fontSize: '13px',
    color: '#6B7280',
  },
  dropdownDivider: {
    height: '1px',
    background: '#E5E7EB',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '14px 16px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: '#EF4444',
    transition: 'background 0.2s',
  },
  main: {
    flex: 1,
    padding: '32px',
    animation: 'fadeIn 0.5s ease-out',
  },
};
